import { useEffect, useMemo, useRef, useState } from 'react';

import ResetIcon from '@/assets/all-icons/reset-icon';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import PublicationSchedulePicker from '@/shared-components/forms/components/PublicationSchedulePicker';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import Text from '@/shared-components/text/components/Text';

import './styles/ArtworkPublicationDatePopup.css';

const MADRID_TIMEZONE = 'Europe/Madrid';

// Reuse the date helpers from the article scheduler to keep behaviour consistent.
function getTimeZoneOffset(date, timeZone) {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return 0;
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  let partsArray;
  try {
    partsArray = formatter.formatToParts(date);
  } catch (error) {
    return 0;
  }

  const parts = partsArray.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );

  return (asUTC - date.getTime()) / 60000;
}

function zonedDateToUtc({ year, month, day, hour, minute, second = 0, timeZone }) {
  const baseUTCDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  const initialOffset = getTimeZoneOffset(baseUTCDate, timeZone);
  const firstPass = new Date(baseUTCDate.getTime() - initialOffset * 60000);

  const verifiedOffset = getTimeZoneOffset(firstPass, timeZone);
  if (verifiedOffset !== initialOffset) {
    return new Date(baseUTCDate.getTime() - verifiedOffset * 60000);
  }

  return firstPass;
}

function formatGmtOffset(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesLabel = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  return `GMT${sign}${String(hours).padStart(2, '0')}${minutesLabel}`;
}

function formatDifferenceLabel(diffMinutes) {
  if (diffMinutes === 0) {
    return '±0h';
  }

  const sign = diffMinutes > 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(diffMinutes);
  const hours = Math.floor(absoluteMinutes / 60);
  const minutes = absoluteMinutes % 60;
  const minutesSuffix = minutes ? ` ${minutes}m` : '';
  return `${sign}${hours}h${minutesSuffix}`;
}

export default function ArtworkPublicationDatePopup({
  open,
  onClose,
  formState,
  setFormState,
  formError,
  setFormError
}) {
  const [dateValue, setDateValue] = useState(formState.publicationDate || '');
  const [timeValue, setTimeValue] = useState(formState.publicationTime || '');
  const lastNonQueuedStatusRef = useRef(
    formState.draft_status && formState.draft_status !== 'queued'
      ? formState.draft_status
      : null
  );

  useEffect(() => {
    if (open) {
      setDateValue(formState.publicationDate || '');
      setTimeValue(formState.publicationTime || '');
    }
  }, [open, formState.publicationDate, formState.publicationTime]);

  useEffect(() => {
    if (formState.draft_status && formState.draft_status !== 'queued') {
      lastNonQueuedStatusRef.current = formState.draft_status;
    }
  }, [formState.draft_status]);

  const todayMin = useMemo(() => new Date().toISOString().split('T')[0], []);
  const userTimeZone = useMemo(() => {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return resolved || 'UTC';
  }, []);

  const timezoneMetrics = useMemo(() => {
    const now = new Date();
    const madridOffset = getTimeZoneOffset(now, MADRID_TIMEZONE);
    const userOffset = getTimeZoneOffset(now, userTimeZone);

    return {
      madridOffset,
      gmtLabel: formatGmtOffset(madridOffset),
      differenceLabel: formatDifferenceLabel(madridOffset - userOffset)
    };
  }, [userTimeZone]);

  const hasScheduleError = Array.isArray(formError)
    ? formError.some(error => error?.error_code === 'INVALID_PUBLICATION_SCHEDULE')
    : formError?.error_code === 'INVALID_PUBLICATION_SCHEDULE';

  const selectedSchedule = useMemo(() => {
    if (!dateValue || !timeValue) return null;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);

    if ([year, month, day, hour, minute].some(Number.isNaN)) {
      return null;
    }

    return { year, month, day, hour, minute };
  }, [dateValue, timeValue]);

  const scheduledUtcDate = useMemo(() => {
    if (!selectedSchedule) return null;

    return zonedDateToUtc({
      ...selectedSchedule,
      timeZone: MADRID_TIMEZONE
    });
  }, [selectedSchedule]);

  const isScheduleInPast = Boolean(scheduledUtcDate && scheduledUtcDate.getTime() <= Date.now());
  const shouldShowScheduleError = hasScheduleError || isScheduleInPast;
  const isSubmitDisabled = !selectedSchedule || shouldShowScheduleError;

  const clearScheduleError = () => {
    if (!setFormError || !hasScheduleError) {
      return;
    }

    setFormError(prevError => {
      if (!prevError) return prevError;

      if (Array.isArray(prevError)) {
        const filteredErrors = prevError.filter(error => error?.error_code !== 'INVALID_PUBLICATION_SCHEDULE');
        return filteredErrors.length ? filteredErrors : null;
      }

      if (prevError?.error_code === 'INVALID_PUBLICATION_SCHEDULE') {
        return null;
      }

      return prevError;
    });
  };

  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleBackgroundClick = (event) => {
    const card = event.currentTarget.querySelector('.generic-popup__card');
    if (card && card.contains(event.target)) {
      return;
    }

    handleClose();
  };

  const handleDateChange = value => {
    setDateValue(value);
    clearScheduleError();
  };

  const handleTimeChange = value => {
    setTimeValue(value);
    clearScheduleError();
  };

  const handleSubmit = () => {
    if (!selectedSchedule || !scheduledUtcDate || isScheduleInPast) {
      return;
    }

    const scheduledOffsetMinutes = getTimeZoneOffset(scheduledUtcDate, MADRID_TIMEZONE);
    const sign = scheduledOffsetMinutes >= 0 ? '+' : '-';
    const abs = Math.abs(scheduledOffsetMinutes);
    const offHH = String(Math.floor(abs / 60)).padStart(2, '0');
    const offMM = String(abs % 60).padStart(2, '0');
    const publishedAtWithOffset = `${dateValue}T${timeValue}${sign}${offHH}:${offMM}`;

    setFormState(prev => ({
      ...prev,
      publicationDate: dateValue,
      publicationTime: timeValue,
      publicationTimezone: MADRID_TIMEZONE,
      publicationOffsetMinutes: scheduledOffsetMinutes,
      publishedAt: publishedAtWithOffset,
    }));

    handleClose();
  };

  const handleReset = () => {
    setDateValue('');
    setTimeValue('');
    setFormState(prev => {
      const restoredStatus =
        prev.draft_status === 'queued'
          ? lastNonQueuedStatusRef.current ?? null
          : prev.draft_status;

      lastNonQueuedStatusRef.current = restoredStatus;

      return {
        ...prev,
        publicationDate: '',
        publicationTime: '',
        publicationTimezone: null,
        publicationOffsetMinutes: null,
        publishedAt: null,
      };
    });
  };

  return (
    <GenericPopup
      open={open}
      className="artwork-publication-date-popup remove-scrollbar"
      opacity
      onBackgroundClick={handleBackgroundClick}
    >
      <div className="generic-popup__header artwork-publication-date-popup__header">
        <button
          type="button"
          className="artwork-publication-date-popup__reset"
          onClick={handleReset}
          aria-label="Reset artwork publication date"
        >
          <ResetIcon className="artwork-publication-date-popup__reset-icon" />
        </button>
        <Text className="artwork-publication-date-popup__title">
          Schedule publication
        </Text>
        <XIcon className="generic-popup__close" onClick={handleClose} />
      </div>

      <div className="artwork-publication-date-popup__body">
        <PublicationSchedulePicker idPrefix="artwork-publication" dateValue={dateValue} timeValue={timeValue} onDateChange={handleDateChange} onTimeChange={handleTimeChange} minimumDate={todayMin} />

        <div className="artwork-publication-date-popup__timezone">
          <Text className="artwork-publication-date-popup__timezone-title">
            Madrid ({timezoneMetrics.gmtLabel})
          </Text>
          <Text className="artwork-publication-date-popup__timezone-hint">
            Your timezone ({userTimeZone}) is {timezoneMetrics.differenceLabel} compared to Madrid.
          </Text>
        </div>
      </div>

      {shouldShowScheduleError && (
        <div className="artwork-publication-date-popup__error" role="alert">
          Scheduled publication time must be in the future.
        </div>
      )}

      <ContinueButton
        className="artwork-publication-date-popup__submit"
        onClick={handleSubmit}
        turnOff={isSubmitDisabled}
      >
        Set date
      </ContinueButton>
    </GenericPopup>
  );
}
